import { useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Loader } from "./components/common";

import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import Onboarding from "./pages/Onboarding";
import BillingSuccess from "./pages/BillingSuccess";
import ResetPassword from "./pages/ResetPassword";

import CustomerLayout from "./customer/CustomerLayout";
import Dashboard from "./customer/Dashboard";
import WorkoutPlayer from "./customer/WorkoutPlayer";
import Program from "./customer/Program";
import Progress from "./customer/Progress";
import Messages from "./customer/Messages";
import Profile from "./customer/Profile";
import Nutrition from "./customer/Nutrition";

import CoachLayout from "./coach/CoachLayout";
import CoachDashboard from "./coach/CoachDashboard";
import Customers from "./coach/Customers";
import CustomerDetail from "./coach/CustomerDetail";
import WorkoutBuilder from "./coach/WorkoutBuilder";
import Programs from "./coach/Programs";
import Exercises from "./coach/Exercises";
import CoachMessages from "./coach/CoachMessages";
import Checkins from "./coach/Checkins";
import Reports from "./coach/Reports";
import Settings from "./coach/Settings";
import CoachManagement from "./coach/CoachManagement";

function AuthCallback() {
  const { completeGoogle } = useAuth();
  const navigate = useNavigate();
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const hash = window.location.hash;
    const sid = new URLSearchParams(hash.replace("#", "")).get("session_id");
    (async () => {
      try {
        const user = await completeGoogle(sid);
        window.location.hash = "";
        navigate(user.role === "customer" ? (user.onboarded ? "/app" : "/onboarding") : "/coach", { replace: true });
      } catch (e) {
        navigate("/login", { replace: true });
      }
    })();
  }, [completeGoogle, navigate]);
  return <Loader />;
}

function Protected({ roles, children }) {
  const { user, loading } = useAuth();
  if (loading) return <Loader />;
  if (!user) return <Navigate to="/login" replace />;
  const userRoles = user.roles || [user.role];
  if (roles && !roles.some((r) => userRoles.includes(r))) {
    return <Navigate to={userRoles.includes("customer") ? "/app" : "/coach"} replace />;
  }
  if (user.role === "customer" && !user.onboarded && window.location.pathname !== "/onboarding") {
    return <Navigate to="/onboarding" replace />;
  }
  return children;
}

function AppRoutes() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/onboarding" element={<Protected roles={["customer"]}><Onboarding /></Protected>} />
      <Route path="/billing/success" element={<BillingSuccess />} />
      <Route path="/reset-password" element={<ResetPassword />} />

      <Route path="/app" element={<Protected roles={["customer"]}><CustomerLayout /></Protected>}>
        <Route index element={<Dashboard />} />
        <Route path="program" element={<Program />} />
        <Route path="nutrition" element={<Nutrition />} />
        <Route path="progress" element={<Progress />} />
        <Route path="messages" element={<Messages />} />
        <Route path="profile" element={<Profile />} />
      </Route>
      <Route path="/app/workout/:id" element={<Protected roles={["customer"]}><WorkoutPlayer /></Protected>} />

      <Route path="/coach" element={<Protected roles={["coach", "admin"]}><CoachLayout /></Protected>}>
        <Route index element={<CoachDashboard />} />
        <Route path="customers" element={<Customers />} />
        <Route path="coaches" element={<CoachManagement />} />
        <Route path="customers/:id" element={<CustomerDetail />} />
        <Route path="builder" element={<WorkoutBuilder />} />
        <Route path="programs" element={<Programs />} />
        <Route path="exercises" element={<Exercises />} />
        <Route path="messages" element={<CoachMessages />} />
        <Route path="checkins" element={<Checkins />} />
        <Route path="reports" element={<Reports />} />
        <Route path="settings" element={<Settings />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Toaster theme="dark" position="bottom-right" richColors />
          <AppRoutes />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
